import React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@heroui/react';
import { Widget } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { BAR_CHART_HEIGHT_PX } from './hourlyActivityMeta';

// Deterministic, hand-tuned heights so the placeholder has a familiar
// "midday bump" silhouette instead of a flat row of identical bars.
const PLACEHOLDER_BAR_HEIGHTS = [
    40, 50, 62, 84, 108, 116, 108, 120, 138, 124, 100, 78, 64,
];

export const HourlyActivitySkeleton: React.FC = () => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            title: t('sales.widgets.hourlyActivity.title'),
        }),
        [t],
    );

    return (
        <section
            aria-busy
            aria-labelledby="hourly-activity-widget-title"
            {...getNodeTestIdProp('hourly-activity-section')}
        >
            <Widget
                className="flex flex-col gap-4"
                {...getNodeTestIdProp('hourly-activity-widget')}
            >
                <Widget.Header
                    className="mt-4"
                    {...getNodeTestIdProp('hourly-activity-widget-header')}
                >
                    <Widget.Title
                        className="flex items-center gap-2"
                        {...getNodeTestIdProp('hourly-activity-widget-title')}
                    >
                        <Skeleton
                            className="size-8 shrink-0 rounded-lg"
                            {...getNodeTestIdProp(
                                'hourly-activity-icon-skeleton',
                            )}
                        />
                        {copy.title}
                        <Skeleton
                            className="ms-auto h-6 w-16 rounded-full"
                            {...getNodeTestIdProp(
                                'hourly-activity-trend-skeleton',
                            )}
                        />
                    </Widget.Title>
                    <Widget.Description
                        {...getNodeTestIdProp(
                            'hourly-activity-widget-subtitle',
                        )}
                    >
                        <Skeleton
                            className="h-3 w-40 rounded-md"
                            {...getNodeTestIdProp(
                                'hourly-activity-widget-subtitle-skeleton',
                            )}
                        />
                    </Widget.Description>
                </Widget.Header>
                <Widget.Content>
                    <ul
                        className="flex items-end justify-between gap-2"
                        style={{ height: BAR_CHART_HEIGHT_PX }}
                        {...getNodeTestIdProp('hourly-activity-chart-skeleton')}
                    >
                        {PLACEHOLDER_BAR_HEIGHTS.map((h, index) => (
                            <li
                                key={index}
                                className="flex min-w-0 flex-1 flex-col items-center justify-end gap-1"
                                {...getNodeTestIdProp(
                                    `hourly-activity-bar-placeholder-${index}`,
                                )}
                            >
                                <Skeleton
                                    className="w-full rounded-t-md"
                                    style={{ height: h }}
                                    {...getNodeTestIdProp(
                                        `hourly-activity-bar-placeholder-fill-${index}`,
                                    )}
                                />
                                <Skeleton
                                    className="mt-1 h-2 w-4 rounded-sm"
                                    {...getNodeTestIdProp(
                                        `hourly-activity-bar-placeholder-tick-${index}`,
                                    )}
                                />
                            </li>
                        ))}
                    </ul>
                </Widget.Content>
            </Widget>
        </section>
    );
};
